
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { Navigate } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, AlertCircle } from "lucide-react";

interface CMSGuardProps {
  children: React.ReactNode;
}

export function CMSGuard({ children }: CMSGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const adminStatus = useAdminStatus();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!adminStatus.canAccessCMS) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to access the CMS. Contact an administrator for access.
            </p>
            <p className="text-sm text-muted-foreground">
              Required role: Content Manager, Admin, or Super Admin
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
